import * as PDFLib from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';

/**
 * Unified Form Field Interface
 * Used for ALL fields (user-created + extracted from PDF)
 * No distinction between "existing" and "new" fields
 */
export interface UnifiedFormField {
  id: string;
  type: 'textfield' | 'checkbox';
  // TWO-PHASE PAGE TRACKING
  pageId: string;    // Stable page UUID (identity)
  pageNum: number;   // Current page position (derived from pageId)
  x: number;
  y: number;
  width: number;
  height: number;
  value?: string | boolean;
  placeholder?: string;
  multiline?: boolean;
  fieldName?: string;
  horizontalAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  fontSize?: number;
  fontColor?: string;
  readOnly?: boolean;
  required?: boolean;
}

// Backward compatibility alias
export type BuilderFormField = UnifiedFormField;

export interface PDFComment {
  id: string;
  // TWO-PHASE PAGE TRACKING
  pageId: string;    // Stable page UUID
  pageNum: number;   // Derived from pageId
  x: number;
  y: number;
  text: string;
  author: string;
  authorId: string;
  createdAt: string;
}

export interface PDFStrikeout {
  id: string;
  // TWO-PHASE PAGE TRACKING
  pageId: string;    // Stable page UUID
  pageNum: number;   // Derived from pageId
  x: number;
  y: number;
  width: number;
  color?: string;
}

export interface PDFFreeText {
  id: string;
  // TWO-PHASE PAGE TRACKING
  pageId: string;    // Stable page UUID
  pageNum: number;   // Derived from pageId
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
  fontSize?: number;
  fontColor?: string;
}

/**
 * Resolve the fieldName for an imported widget.
 * Preserves shared names across widgets — that's how PDF links fields (§12.7.3.1).
 * Synthesizes a name only when the source PDF has none (so unnamed widgets stay solo).
 */
function resolveFieldName(baseName: string | undefined): string {
  if (!baseName) return `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  return baseName;
}

/**
 * Extract form fields from PDF with pageId assignment
 * @param pdfBytes - PDF file bytes
 * @param pageIdMap - Map of pageNum -> pageId for Two-Phase tracking (optional for backward compatibility)
 * @param existingFields - Existing fields to check for fieldName conflicts (for deduplication across document)
 */
export async function extractFieldsFromPDF(
  pdfBytes: Uint8Array,
  pageIdMap?: Map<number, string>,
  existingFields?: UnifiedFormField[]
): Promise<UnifiedFormField[]> {
  const fields: UnifiedFormField[] = [];

  try {
    const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
    const pdfDoc = await loadingTask.promise;

    for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
      const page = await pdfDoc.getPage(pageNum);
      const annotations = await page.getAnnotations();
      // Use scale 1.0 to get native PDF coordinates (in points)
      const viewport = page.getViewport({ scale: 1.0 });

      // Get pageId for this page, or generate temp one if map not provided
      const pageId = pageIdMap?.get(pageNum) ?? `temp-page-${pageNum}`;

      annotations.forEach((annotation) => {
        if (annotation.subtype !== 'Widget') return;
        if (!annotation.rect) return;

        const rect = annotation.rect;
        // Store coordinates in PDF points (no scaling)
        // PDF coords: [x1, y1, x2, y2] where y1 < y2 (bottom-left origin)
        const x = rect[0];
        const y = viewport.height - rect[3]; // Convert to top-left origin
        const width = rect[2] - rect[0];
        const height = rect[3] - rect[1];

        if (annotation.fieldType === 'Tx') {
          fields.push({
            id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: 'textfield',
            pageId,
            pageNum,
            x,
            y,
            width,
            height,
            value: annotation.fieldValue || '', // Extract initial value from PDF
            // Placeholder = PDF's user-facing tooltip (/TU), NOT the internal
            // field name. fieldName is often author-tool-generated junk like
            // `topmostSubform[0].Page1[0].Client[0]` — not user-friendly text.
            placeholder: (annotation as any).alternativeText || '',
            fieldName: resolveFieldName(annotation.fieldName),
          });
        } else if (annotation.fieldType === 'Btn' && annotation.checkBox) {
          // Normalize checkbox value to boolean (PDF.js can return various truthy values)
          const rawValue = annotation.fieldValue;
          const isChecked = rawValue === true || rawValue === 'true' || rawValue === 1 ||
                           rawValue === 'Yes' || rawValue === 'On' || rawValue === 'yes' || rawValue === 'on';

          fields.push({
            id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: 'checkbox',
            pageId,
            pageNum,
            x,
            y,
            width,
            height,
            value: isChecked, // Always store as boolean for consistency
            fieldName: resolveFieldName(annotation.fieldName),
          });
        }
      });
    }
  } catch (error) {
    console.error('Error extracting PDF fields:', error);
  }

  return fields;
}

export async function embedFieldsInPDF(
  pdfBytes: Uint8Array,
  fields: UnifiedFormField[],
  comments?: PDFComment[],
  strikeouts?: PDFStrikeout[],
  freeTexts?: PDFFreeText[]
): Promise<Uint8Array> {
  const pdfDoc = await PDFLib.PDFDocument.load(pdfBytes);
  const pages = pdfDoc.getPages();

  // Strip any pre-existing AcroForm + widget annotations from the source PDF.
  // Why: allFields is the single source of truth — we rebuild the form from scratch.
  // Using form.removeField() breaks on source PDFs whose widgets lack normal
  // appearance streams (/AP /N), throwing "Unexpected N type: undefined" and
  // leaving the form in an inconsistent state that fails later during save.
  // Editing the catalog directly bypasses pdf-lib's appearance-stream lookups.
  const catalog = pdfDoc.catalog;
  catalog.delete(PDFLib.PDFName.of('AcroForm'));

  const widgetName = PDFLib.PDFName.of('Widget');
  const subtypeName = PDFLib.PDFName.of('Subtype');
  const annotsName = PDFLib.PDFName.of('Annots');

  pages.forEach((page) => {
    const annotsObj = page.node.Annots();
    if (!annotsObj) return;
    const annots = annotsObj.asArray();
    const nonWidgets = annots.filter((annotRef) => {
      try {
        const annotDict = pdfDoc.context.lookup(annotRef, PDFLib.PDFDict);
        const subtype = annotDict?.get(subtypeName);
        return subtype !== widgetName;
      } catch {
        // If we can't resolve the annot (broken ref), keep it — not ours to touch.
        return true;
      }
    });
    if (nonWidgets.length === 0) {
      page.node.delete(annotsName);
    } else if (nonWidgets.length !== annots.length) {
      page.node.set(annotsName, pdfDoc.context.obj(nonWidgets));
    }
  });

  // Now get a fresh (empty) form to add our fields into
  const form = pdfDoc.getForm();

  // Embed ALL fields. Widgets sharing a user-authored fieldName become a single
  // logical PDF field with multiple widgets (native PDF spec linking §12.7.3.1).
  // Auto-generated names (`field_…`) stay solo — one widget per logical field.
  const groups = new Map<string, UnifiedFormField[]>();
  fields.forEach((field) => {
    const pageIndex = field.pageNum - 1;
    if (pageIndex < 0 || pageIndex >= pages.length) return;
    const linkable = !!field.fieldName && !field.fieldName.startsWith('field_');
    const key = linkable ? `${field.type}:${field.fieldName}` : `solo:${field.id}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(field);
    else groups.set(key, [field]);
  });

  const addWidgetsForGroup = (
    widgets: UnifiedFormField[],
    placeOnPage: (widget: UnifiedFormField, page: PDFLib.PDFPage, rect: { x: number; y: number; width: number; height: number }) => void
  ) => {
    widgets.forEach((w) => {
      const page = pages[w.pageNum - 1];
      if (!page) return;
      placeOnPage(w, page, {
        x: w.x,
        y: page.getHeight() - w.y - w.height,
        width: w.width,
        height: w.height,
      });
    });
  };

  groups.forEach((widgets) => {
    const first = widgets[0];
    if (first.type === 'checkbox') {
      const checkbox = form.createCheckBox(first.fieldName || `checkbox_${first.id}`);
      addWidgetsForGroup(widgets, (_w, page, rect) => {
        checkbox.addToPage(page, { ...rect, borderWidth: 0 });
      });
      const isChecked = first.value === true || first.value === 'true' || (first.value as any) === 1;
      if (isChecked) checkbox.check();
      else checkbox.uncheck();
    } else if (first.type === 'textfield') {
      const textField = form.createTextField(first.fieldName || `textfield_${first.id}`);
      addWidgetsForGroup(widgets, (_w, page, rect) => {
        textField.addToPage(page, { ...rect, borderWidth: 0 });
      });
      if (typeof first.value === 'string') textField.setText(first.value);
      if (first.multiline) textField.enableMultiline();
      if (first.horizontalAlign) {
        const alignment = first.horizontalAlign === 'left' ? PDFLib.TextAlignment.Left :
                         first.horizontalAlign === 'center' ? PDFLib.TextAlignment.Center :
                         PDFLib.TextAlignment.Right;
        textField.setAlignment(alignment);
      }
      if (first.fontSize) textField.setFontSize(first.fontSize);
    }
  });

  strikeouts?.forEach((strikeout) => {
    const pageIndex = strikeout.pageNum - 1;
    if (pageIndex < 0 || pageIndex >= pages.length) return;

    const page = pages[pageIndex];
    const pageHeight = page.getHeight();

    // Coordinates already in PDF points
    const pdfX = strikeout.x;
    const pdfY = pageHeight - strikeout.y;
    const pdfWidth = strikeout.width;

    let r = 0.86, g = 0.21, b = 0.27;
    if (strikeout.color?.startsWith('#')) {
      const hex = strikeout.color.slice(1);
      r = parseInt(hex.substr(0, 2), 16) / 255;
      g = parseInt(hex.substr(2, 2), 16) / 255;
      b = parseInt(hex.substr(4, 2), 16) / 255;
    }

    page.drawLine({
      start: { x: pdfX, y: pdfY },
      end: { x: pdfX + pdfWidth, y: pdfY },
      thickness: 1.5,
      color: PDFLib.rgb(r, g, b),
      opacity: 1.0,
    });
  });

  // Note: Comments are excluded from PDF download
  // pdf-lib doesn't support proper PDF comment annotations (sticky notes)
  // Comments remain in the database and are visible in the web viewer only
  // If needed in future, would require a different PDF library that supports annotations

  freeTexts?.forEach((freeText) => {
    const pageIndex = freeText.pageNum - 1;
    if (pageIndex < 0 || pageIndex >= pages.length) return;

    const page = pages[pageIndex];
    const pageHeight = page.getHeight();

    // Coordinates already in PDF points
    const pdfX = freeText.x;
    const pdfY = pageHeight - freeText.y - freeText.height;

    if (freeText.text && freeText.text !== 'Click to edit text') {
      const fontSize = freeText.fontSize || 14;

      let r = 0, g = 0, b = 0;
      if (freeText.fontColor?.startsWith('#')) {
        const hex = freeText.fontColor.slice(1);
        r = parseInt(hex.substr(0, 2), 16) / 255;
        g = parseInt(hex.substr(2, 2), 16) / 255;
        b = parseInt(hex.substr(4, 2), 16) / 255;
      }

      page.drawText(freeText.text, {
        x: pdfX,
        y: pdfY,
        size: fontSize,
        color: PDFLib.rgb(r, g, b),
        maxWidth: freeText.width,
      });
    }
  });

  // Set /NeedAppearances on the AcroForm so any PDF viewer regenerates
  // field appearance streams on first open — defensive fallback in case
  // pdf-lib's inline appearance generation misses anything.
  const acroFormDict = pdfDoc.catalog.get(PDFLib.PDFName.of('AcroForm'));
  if (acroFormDict instanceof PDFLib.PDFDict) {
    acroFormDict.set(PDFLib.PDFName.of('NeedAppearances'), PDFLib.PDFBool.True);
  }

  // updateFieldAppearances: false skips pdf-lib's post-save appearance pass,
  // which can crash on edge-case widgets. Our fields were built programmatically
  // with pdf-lib's API (setText / check), so their appearances are already set.
  return await pdfDoc.save({ updateFieldAppearances: false });
}
